import type {
  ComponentLineItemGroup,
  ParentLineItemGroup,
} from "~/domain/pricing-engine/index.server";
import type { CombinedLineItemGroup } from "~/features/process-overview/pages/product-mapping/types";
import type { UiProductDimension } from "./types";

export function formatNumber(amount: number | null): string {
  if (amount === null) {
    amount = 0;
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatCurrency(amount: number | null): string | null {
  if (amount === null) {
    return null;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatRange(
  min: number | null,
  max: number | null,
  formatter: (n: number | null) => string | null,
): string | null {
  if (min === null && max === null) {
    return null;
  }
  if (min === max) {
    return formatter(min);
  }
  return `${formatter(min)} - ${formatter(max)}`;
}

export function productDimensionIsPlaceholder(
  product: UiProductDimension,
): boolean {
  return (
    (product.id === "optimistic-product-dimension" ||
      product.id === "optimistic-component-product-dimension") &&
    product.state === "none"
  );
}

export function getGroupKey(group?: {
  lineItemGroupId?: string | null;
  lineItemGroupName?: string | null;
}) {
  return group?.lineItemGroupId ?? group?.lineItemGroupName ?? "";
}

export function validateSameCompany<T extends { companyId: string }>(
  items: T[],
): string {
  if (items.length === 0) {
    throw new Error("No items provided");
  }

  const firstCompanyId = items[0].companyId;
  const allSameCompany = items.every(
    (item) => item.companyId === firstCompanyId,
  );

  if (!allSameCompany) {
    throw new Error("All items must belong to the same company");
  }

  return firstCompanyId;
}

export function sanitizeFormData(formData: FormData): FormData {
  const sanitizedFormData = new FormData();

  for (const [key, value] of formData.entries()) {
    if (typeof value === "string") {
      sanitizedFormData.append(key, value.trim());
    } else {
      sanitizedFormData.append(key, value);
    }
  }

  return sanitizedFormData;
}

// SAVE for debugging
// export function printFormDataStrings(formData: FormData) {
//   for (const [key, value] of formData.entries()) {
//     if (typeof value === "string") {
//       console.log(`${key}: ${value}`);
//     }
//   }
// }

export function convertParentLineItemGroupToCommonGroup(
  group: ParentLineItemGroup | undefined,
): CombinedLineItemGroup | undefined {
  if (!group) return undefined;
  return {
    key: `${group.lineItemGroupId || ""}`,
    id: group.lineItemGroupId,
    name: group.lineItemGroupName,
    groupType: group.lineItemGroupType,
    groupSubType: group.lineItemGroupSubType,
    productDimensionId: group.productDimensionId,
    productDimensionName: group.productDimensionName,
    serviceNames: group.serviceNames,
    lineItemCount: group.lineItemCount,
    documentCount: group.documentCount,
    recentDocumentCount: group.recentDocumentCount,
    documentIds: group.documentIds,
    avgDocumentLineItemCount: group.avgDocumentLineItemCount,
    minDocumentLineItemCount: group.minDocumentLineItemCount,
    maxDocumentLineItemCount: group.maxDocumentLineItemCount,
    minQuantity: group.minQuantity,
    medianQuantity: group.medianQuantity,
    maxQuantity: group.maxQuantity,
    minTotalLineItemPriceAmount: group.minTotalLineItemPriceAmount,
    maxTotalLineItemPriceAmount: group.maxTotalLineItemPriceAmount,
    priceNonZeroFrequency: group.priceNonZeroFrequency,
    minAnnualizedTotalLineItemPriceAmount:
      group.minAnnualizedTotalLineItemPriceAmount,
    maxAnnualizedTotalLineItemPriceAmount:
      group.maxAnnualizedTotalLineItemPriceAmount,
    minAnnualizedUnitSalesPriceAmount: group.minAnnualizedUnitSalesPriceAmount,
    maxAnnualizedUnitSalesPriceAmount: group.maxAnnualizedUnitSalesPriceAmount,
    frequency: null,
    lineItemGroupId: group.lineItemGroupId,
    lineItemGroupName: group.lineItemGroupName,
  };
}

export function convertComponentLineItemGroupToCommonGroup(
  group: ComponentLineItemGroup | undefined,
): CombinedLineItemGroup | undefined {
  if (!group) return undefined;

  const {
    lineItemComponentGroupId,
    lineItemComponentGroupName,
    lineItemComponentGroupType,
    lineItemComponentGroupSubType,
    ...rest
  } = group;

  return {
    key: lineItemComponentGroupId ?? lineItemComponentGroupName ?? "",
    id: lineItemComponentGroupId,
    name: lineItemComponentGroupName,
    groupType: lineItemComponentGroupType,
    groupSubType: lineItemComponentGroupSubType,
    ...rest,
  };
}

export function normalizeGroupKey(key: string): string {
  return key.replace(/\s|[^a-z0-9]/gi, "").toLowerCase();
}

export function getDimensionFormKey(
  productDimension: UiProductDimension,
  suffix?: string,
) {
  const baseKey =
    "updatedAt" in productDimension
      ? `${productDimension.updatedAt?.toISOString()}-${productDimension.id}`
      : productDimension.id;
  return suffix ? `${baseKey}-${suffix}` : baseKey;
}
